import React, { useEffect, useState, useRef } from 'react';
import ContractIdCard from './ContractIdCard';
import Breadcrumb from '../../../layouts/full-layout/breadcrumb/Breadcrumb';
import PageContainer from '../../../components/container/PageContainer';
import FeatherIcon from 'feather-icons-react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Checkbox,
    ListItemText,
    TextField,
    ListSubheader,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { GetAllEmployeeDetailsDropdowns } from '../../../services/human-resource';
import { GetUserDetail } from '../../../services/user';
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const initialData = {
    empName: 'Bhargav Shukla',
    empId: '8063',
    empPhotoUrl: 'https://hrms.sswhite.co/pic/8063.png',
};

const BCrumb = [
    { to: '/', title: 'HR Management' },
    { title: 'Contract Worker ID Card' },
];

export default function ContractIdCardPage() {
    const [mode, setMode] = useState('single');
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [exporting, setExporting] = useState(false);
    const wrapperRef = useRef(null);
    // Search state for employee dropdown
    const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
    const searchInputRef = useRef(null);

    const filteredEmployees = employees.filter(emp =>
        selectedEmployees.includes(emp.empId)
    );

    useEffect(() => {
        loadEmployees();
        loadLoggedInEmployee();
    }, []);

    // Filter employees based on search term - search by name OR ID
    const getFilteredEmployees = () => {
        if (!employeeSearchTerm.trim()) return employees;
        const searchLower = employeeSearchTerm.toLowerCase().trim();
        return employees.filter(emp => {
            const nameMatch = emp.empName?.toLowerCase().includes(searchLower) || false;
            const idMatch = String(emp.empId).includes(searchLower);
            return nameMatch || idMatch;
        });
    };

    const loadLoggedInEmployee = async () => {
        try {
            const userRes = await GetUserDetail();
            const personId = String(userRes.data.personId);

            setSelectedEmployees([personId]);
        } catch (err) {
            console.error(err);
        }
    };

    const loadEmployees = async () => {
        try {
            setLoading(true);

            const { data } = await GetAllEmployeeDetailsDropdowns(true);

            const employeeList = (data?.buddyDropDown || []).map((emp) => ({
                ...initialData,
                empName: emp.buddyName,
                empId: emp.personID,
                userID: emp.userID,
                empPhotoUrl: `https://hrms.sswhite.co/pic/${emp.personID}.png`,
            }));

            setEmployees(employeeList);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const captureCardElement = async (element) => {
        const images = [...element.querySelectorAll("img")];

        await Promise.all(
            images.map(img =>
                img.complete
                    ? Promise.resolve()
                    : new Promise(resolve => {
                        img.onload = resolve;
                        img.onerror = resolve;
                    })
            )
        );

        return html2canvas(element, {
            scale: 4,
            useCORS: true,
            allowTaint: false,
            backgroundColor: "#fff",
        });
    };

    const exportSinglePDF = async () => {
        try {
            setExporting(true);

            const card =
                wrapperRef.current.querySelector(".contract-id-card-face");

            const canvas = await captureCardElement(card);

            const pdf = new jsPDF("p", "mm", "a4");

            const pageWidth = 210;
            const pageHeight = 297;

            const cardWidth = 70;
            const cardHeight = 118;

            const startX = (pageWidth - cardWidth) / 2;
            const startY = (pageHeight - cardHeight) / 2;

            pdf.addImage(
                canvas,
                "PNG",
                startX,
                startY,
                cardWidth,
                cardHeight
            );

            const employee =
                filteredEmployees[0] || employees.find(e => String(e.empId) === String(selectedEmployees[0]));

            pdf.save(`Contract_ID_${employee?.empId || "Employee"}.pdf`);
        } finally {
            setExporting(false);
        }
    };

    const exportBulkPDF = async () => {
        try {
            setExporting(true);

            const cards = document.querySelectorAll(".contract-id-card-bulk-item");

            if (!cards.length) return;

            const pdf = new jsPDF("p", "mm", "a4");

            const pageWidth = 210;
            const pageHeight = 297;

            const cardWidth = 70;
            const cardHeight = 118;

            const startX = (pageWidth - cardWidth) / 2;
            const startY = (pageHeight - cardHeight) / 2;

            const container = document.querySelector(".contract-id-card-bulk");

            const canvas = await html2canvas(container, {
                scale: 3,
                useCORS: true,
                backgroundColor: "#fff",
            });

            const imgData = canvas.toDataURL("image/png");

            const pdfWidth = 210;
            const pdfHeight = 297;

            const imgWidth = pdfWidth;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            let heightLeft = imgHeight;
            const topMargin = 1; // 10 mm (1 cm)
            let position = topMargin;

            pdf.addImage(
                imgData,
                "PNG",
                0,
                position,
                imgWidth,
                imgHeight
            );

            // Reduce available height because of top margin
            heightLeft -= (pdfHeight - topMargin);

            while (heightLeft > 0) {

                position = heightLeft - imgHeight + topMargin;

                pdf.addPage();

                pdf.addImage(
                    imgData,
                    "PNG",
                    0,
                    position,
                    imgWidth,
                    imgHeight
                );

                heightLeft -= (pdfHeight - topMargin);
            }
            heightLeft -= pdfHeight;

            while (heightLeft > 0) {
                position = heightLeft - imgHeight;

                pdf.addPage();
                pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);

                heightLeft -= pdfHeight;
            }

            pdf.save("Contract_ID_Bulk.pdf");

        } catch (err) {
            console.error(err);
            alert("Error exporting PDF");
        } finally {
            setExporting(false);
        }
    };

    const printStyles = `
@media print {

  @page {
    size: A4 portrait;
    margin: 8mm;
  }

  body * {
    visibility: hidden;
  }

  .contract-id-card-page,
  .contract-id-card-page * {
    visibility: visible;
  }

  .contract-id-card-page {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    background: #fff;
  }

  .contract-id-card-page__toolbar,
  .contract-id-card-page__breadcrumb {
    display: none !important;
  }

  body {
    margin: 0;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* SINGLE */
  .contract-id-card-print {
    display: flex !important;
    justify-content: center !important;
    width: 100% !important;
  }

  /* BULK */
.contract-id-card-bulk {
    display: flex !important;
    flex-wrap: wrap !important;
    justify-content: center !important;
    align-content: flex-start !important;
    gap: 6mm !important;
    width: 100% !important;
}

.contract-id-card-bulk-item {
    display: flex !important;
    justify-content: center !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
}

/* SAME SIZE AS SINGLE */
.contract-id-card-bulk-item .contract-id-card-scene {
    zoom: 0.76 !important;
    perspective: none !important;
    display: inline-block !important;
    width: auto !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
}

/* BULK PREVIEW: intentionally identical to SINGLE card scale */
.contract-id-card-bulk-item .contract-id-card-face {
    position: relative !important;
    display: block !important;
    width: 380px !important;
    height: 640px !important;
    transform: none !important;
    transform-origin: top left !important;
    box-shadow: none !important;
    overflow: hidden !important;
}

  /* CARD - SAME SIZE AS OFFICE */
.contract-id-card-scene {
    zoom: 0.76 !important;
    perspective: none !important;
    display: inline-block !important;
    width: auto !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
}

.contract-id-card-face {
    position: relative !important;
    display: block !important;
    width: 380px !important;
    height: 640px !important;
    transform: none !important;
    transform-origin: top left !important;
    box-shadow: none !important;
    overflow: hidden !important;
}

}
        `;

    return (
        <>
            <style>{printStyles}</style>
            <Box className="contract-id-card-page" sx={{ minHeight: 'calc(100vh - 120px)', p: 3, background: 'linear-gradient(135deg, #f8fbff 0%, #eef6ff 100%)' }}>
                <PageContainer title="Contract Worker ID Card">
                    <Card sx={{ p: 0, borderRadius: '20px', overflow: 'hidden' }}>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            p={2}
                            sx={{
                                backgroundColor: 'primary.light',
                                color: 'primary.main',
                                borderTopLeftRadius: '20px',
                                borderTopRightRadius: '20px',
                                borderBottom: '1px solid #e6eef5',
                                flexWrap: 'wrap',
                                gap: 1,
                            }}
                            className="contract-id-card-page__toolbar"
                        >
                            <Box display="flex" alignItems="center">
                                <FeatherIcon icon="user" width="18" />
                                <Typography component="span" sx={{ ml: 1, fontWeight: 600 }}>Contract Worker ID Card</Typography>
                            </Box>

                            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">

                                {/* Mode Toggle */}
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={mode}
                                    color="primary"
                                    onChange={async (_, value) => {
                                        if (!value) return;

                                        setMode(value);
                                        setEmployeeSearchTerm("");

                                        if (value === "single") {
                                            await loadLoggedInEmployee();
                                        } else {
                                            setSelectedEmployees(prev =>
                                                prev.length ? prev : [String(selectedEmployees[0])]
                                            );
                                        }
                                    }}
                                    sx={{
                                        height: 40,
                                        backgroundColor: '#f0f7ff',
                                        borderRadius: '30px',
                                        padding: '3px',
                                        border: '1px solid #dbeafe',
                                        '& .MuiToggleButton-root': {
                                            px: 3,
                                            py: 1,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            borderRadius: '30px !important',
                                            border: 'none',
                                            color: '#94a3b8',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                                color: 'primary.main',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: 'primary.main',
                                                color: '#ffffff',
                                                '&:hover': {
                                                    backgroundColor: 'primary.main',
                                                }
                                            },
                                            '&.Mui-selected + &': {
                                                borderLeft: 'none',
                                            },
                                            '&:not(:first-of-type)': {
                                                borderLeft: 'none',
                                            },
                                        }
                                    }}
                                >
                                    <ToggleButton value="single">
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <FeatherIcon
                                                icon="user"
                                                size={14}
                                                style={{
                                                    opacity: mode === 'single' ? 1 : 0.6,
                                                    transition: 'opacity 0.3s ease'
                                                }}
                                            />
                                            Single
                                        </Box>
                                    </ToggleButton>

                                    <ToggleButton value="bulk">
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <FeatherIcon
                                                icon="users"
                                                size={14}
                                                style={{
                                                    opacity: mode === 'bulk' ? 1 : 0.6,
                                                    transition: 'opacity 0.3s ease'
                                                }}
                                            />
                                            Bulk
                                        </Box>
                                    </ToggleButton>
                                </ToggleButtonGroup>

                                {/* Employees Dropdown with Search */}
                                <FormControl
                                    size="small"
                                    sx={{
                                        width: 300,
                                        minWidth: 300,
                                        maxWidth: 300,
                                        flexShrink: 0,
                                    }}
                                >
                                    <InputLabel>Employees</InputLabel>

                                    {mode === "single" ? (
                                        <Select
                                            value={selectedEmployees[0] || ""}
                                            label="Employees"
                                            onChange={(e) => {
                                                setSelectedEmployees([e.target.value]);
                                                setEmployeeSearchTerm(''); // Clear search on selection
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    className: 'employee-dropdown-paper',
                                                    sx: {
                                                        maxHeight: '320px !important',
                                                        overflowY: 'auto !important',
                                                        overflowX: 'hidden !important',
                                                    },
                                                },
                                                disablePortal: false,
                                                keepMounted: true,
                                            }}
                                            renderValue={(selected) => {
                                                const emp = employees.find(x => String(x.empId) === String(selected));
                                                return emp ? `${emp.empName} (${emp.empId})` : '';
                                            }}
                                        >
                                            {/* Search Input - Sticky at top */}
                                            <ListSubheader
                                                className="employee-search-input"
                                                sx={{
                                                    pointerEvents: 'auto',
                                                    background: 'white',
                                                    padding: '8px 16px',
                                                    position: 'sticky',
                                                    top: 0,
                                                    zIndex: 1,
                                                    borderBottom: '1px solid #e0e0e0',
                                                }}
                                            >
                                                <TextField
                                                    inputRef={searchInputRef}
                                                    size="small"
                                                    placeholder="Search by name or ID..."
                                                    value={employeeSearchTerm}
                                                    onChange={(e) => {
                                                        setEmployeeSearchTerm(e.target.value);
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                    }}
                                                    onKeyDown={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                    onFocus={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                    fullWidth
                                                    autoFocus
                                                    sx={{
                                                        '& .MuiInputBase-root': {
                                                            fontSize: '0.875rem',
                                                            backgroundColor: '#f5f5f5',
                                                            borderRadius: '8px',
                                                            '&:hover': {
                                                                backgroundColor: '#eeeeee',
                                                            },
                                                            '&.Mui-focused': {
                                                                backgroundColor: 'white',
                                                            }
                                                        }
                                                    }}
                                                />
                                            </ListSubheader>

                                            {/* Employee List */}
                                            {getFilteredEmployees().length === 0 ? (
                                                <MenuItem disabled sx={{ py: 2 }}>
                                                    No employees found
                                                </MenuItem>
                                            ) : (
                                                getFilteredEmployees().map((emp) => (
                                                    <MenuItem
                                                        key={emp.userID}
                                                        value={String(emp.empId)}
                                                        sx={{ py: 1 }}
                                                    >
                                                        {emp.empName} ({emp.empId})
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Select>
                                    ) : (
                                        <Select
                                            multiple
                                            value={selectedEmployees}
                                            label="Employees"
                                            onChange={(e) => {
                                                const ids =
                                                    typeof e.target.value === "string"
                                                        ? e.target.value.split(",")
                                                        : e.target.value;
                                                setSelectedEmployees(ids);
                                                setEmployeeSearchTerm(''); // Clear search on selection
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    className: 'employee-dropdown-paper',
                                                    sx: {
                                                        maxHeight: '320px !important',
                                                        overflowY: 'auto !important',
                                                        overflowX: 'hidden !important',
                                                    },
                                                },
                                                disablePortal: false,
                                                keepMounted: true,
                                            }}
                                            renderValue={(selected) =>
                                                employees
                                                    .filter(emp =>
                                                        selected.includes(String(emp.empId))
                                                    )
                                                    .map(emp => emp.empName)
                                                    .join(", ")
                                            }
                                        >
                                            {/* Search Input - Sticky at top for bulk */}
                                            <ListSubheader
                                                className="employee-search-input"
                                                sx={{
                                                    pointerEvents: 'auto',
                                                    background: 'white',
                                                    padding: '8px 16px',
                                                    position: 'sticky',
                                                    top: 0,
                                                    zIndex: 1,
                                                    borderBottom: '1px solid #e0e0e0',
                                                }}
                                            >
                                                <TextField
                                                    inputRef={searchInputRef}
                                                    size="small"
                                                    placeholder="Search by name or ID..."
                                                    value={employeeSearchTerm}
                                                    onChange={(e) => {
                                                        setEmployeeSearchTerm(e.target.value);
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                    }}
                                                    onKeyDown={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                    onFocus={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                    fullWidth
                                                    autoFocus
                                                    sx={{
                                                        '& .MuiInputBase-root': {
                                                            fontSize: '0.875rem',
                                                            backgroundColor: '#f5f5f5',
                                                            borderRadius: '8px',
                                                            '&:hover': {
                                                                backgroundColor: '#eeeeee',
                                                            },
                                                            '&.Mui-focused': {
                                                                backgroundColor: 'white',
                                                            }
                                                        }
                                                    }}
                                                />
                                            </ListSubheader>

                                            {/* Employee List with Checkboxes */}
                                            {getFilteredEmployees().length === 0 ? (
                                                <MenuItem disabled sx={{ py: 2 }}>
                                                    No employees found
                                                </MenuItem>
                                            ) : (
                                                getFilteredEmployees().map((emp) => (
                                                    <MenuItem
                                                        key={emp.userID}
                                                        value={String(emp.empId)}
                                                        sx={{ py: 1 }}
                                                    >
                                                        <Checkbox
                                                            checked={selectedEmployees.includes(
                                                                String(emp.empId)
                                                            )}
                                                        />
                                                        <ListItemText
                                                            primary={`${emp.empName} (${emp.empId})`}
                                                        />
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Select>
                                    )}
                                </FormControl>

                                <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    onClick={() => window.print()}
                                >
                                    {mode === 'bulk' ? 'Bulk Print' : 'Print Card'}
                                </Button>

                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<FeatherIcon icon="download" />}
                                    onClick={() =>
                                        mode === "single"
                                            ? exportSinglePDF()
                                            : exportBulkPDF()
                                    }
                                    disabled={exporting}
                                >
                                    {exporting ? "Exporting..." : "Export PDF"}
                                </Button>

                            </Box>
                        </Box>

                        <CardContent sx={{ p: 3, background: '#f4f8ff' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                {mode === 'single' ? (
                                    <div
                                        ref={wrapperRef}
                                        className="contract-id-card-print"
                                    >
                                        {(filteredEmployees[0] || employees[0]) && (
                                            <ContractIdCard
                                                data={filteredEmployees[0] || employees[0]}
                                            />
                                        )}
                                    </div>
                                ) : (
                                    <Box
                                        className="contract-id-card-bulk"
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 2,
                                            justifyContent: 'center',
                                            width: '100%',
                                        }}
                                    >
                                        {filteredEmployees.map((emp) => (
                                            <Box key={emp.empId} className="contract-id-card-bulk-item">
                                                <ContractIdCard data={emp} />
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </PageContainer>
            </Box>
        </>
    );
}